import React from "react";
import Modal from "react-bootstrap/Modal";
import {getManager} from "../../../ts/manager/Manager";
import AppComponent, {IAppComponentProps} from "../AppComponent";
import AppButton from "../../element/button/AppButton";
import gsap from "gsap";

export interface IBasicModalProps extends IAppComponentProps {
    modalTitle?: string;
    onModalHidden?: (didSubmit:boolean, returnValue:any|null) => void;
}

type ModalTransition = "left" | "right";

export default class BasicModal extends AppComponent<IBasicModalProps> {

    props:IBasicModalProps;
    submitDisabled:boolean = false
    isHideModal:boolean = false;
    hideFooter:boolean = false;

    constructor(props: IBasicModalProps)
    {
        super(props);

        this.props = props;
    }

    bindFunctions()
    {
        super.bindFunctions();
        this.hideModal = this.hideModal.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleAccept = this.handleAccept.bind(this);
    }

    modalTitle(): string
    {
        return this.props.modalTitle ??"Modal"
    }

    hideModal(didSubmit?:boolean, withValue?:any)
    {
        setTimeout(() => {
            this.onModalHidden(didSubmit, withValue);
            this.refreshState();
        },1)
    }

    handleCancel(){
        this.hideModal();
    }

    handleAccept(value?:any): void
    {
        this.hideModal(true, value);
    }

    onModalHidden = (didSubmit?:boolean, withValue?:any) =>
    {
        didSubmit = !!didSubmit;
        this.isHideModal = true;
        if(this.props.onModalHidden)
        {
            const value = withValue ?? (didSubmit ? this.onSubmitReturnValue() : this.onAnyReturnValue());
            this.props.onModalHidden(didSubmit, value);
        }
    }

    onSubmitReturnValue():any|null
    {
        return this.onAnyReturnValue();
    }

    onAnyReturnValue():any|null
    {
        return null;
    }


    componentDidMount()
    {
        super.componentDidMount();
        this.resetTransitions()
    }

    componentWillUnmount()
    {
        this.resetTransitions();
        super.componentWillUnmount();
    }

    getModalContent():Element|null
    {
        return document.querySelectorAll('.modal .modal-dialog .modal-content')[0] ?? null;
    }

    resetTransitions(): void
    {
        const modalContent:Element|null = this.getModalContent();
        if(!modalContent)
        {
            return
        }
        gsap.killTweensOf(modalContent);
        gsap.set(modalContent, {
            clearProps:  "all",
        })
    }

    transitionModal(transition:ModalTransition, midpointCallback:() => void, endpointCallback?:() => void)
    {
        const modalContent:Element|null = this.getModalContent();

        let transitionToMarginLeft:string|null = null;
        let transitionFromMarginLeft:string|null = null;

        if(modalContent)
        {
            if(transition === "right"){
                transitionToMarginLeft = "100vw";
                transitionFromMarginLeft = "-100vw";
            } else if(transition === "left"){
                transitionToMarginLeft = "-100vw";
                transitionFromMarginLeft = "100vw";
            }
        }


        if(!transitionToMarginLeft || !transitionFromMarginLeft)
        {
            midpointCallback();
            if(endpointCallback){
                endpointCallback();
            }
            return
        }

        const timeline = gsap.timeline();
        timeline.to(modalContent, {
            duration: 0.5,
            marginLeft: transitionToMarginLeft,
            onComplete: () => {
                gsap.set(modalContent, {
                    marginLeft: transitionFromMarginLeft
                })
                midpointCallback();
            }
        });

        timeline.to(modalContent, {
            opacity: 1,
            duration: 0.5,
            marginLeft: 0,
            onComplete: () => {
                gsap.set(modalContent, {
                    clearProps:  "all",
                })
                if(endpointCallback){
                    endpointCallback();
                }
            }
        });
    }

    renderBody(): React.JSX.Element
    {
        return <></>
    }

    renderFooterLeft(): React.JSX.Element
    {
        return <></>;
    }

    renderFooterRight(): React.JSX.Element
    {
        return this.okFooterButtons();
    }

    noYesFooterButtons(): React.JSX.Element
    {
        const isDisabled = this.isDisabled();
        return <div
            key={this.loading ? "1" : "0"}
        >
            <AppButton
                className="btn btn-secondary"
                disabled={isDisabled}
                onClick={() => this.handleAccept(false)}
            >
                <>
                    {this.loading &&
                        <span className="spinner-border spinner-border-sm me-2"/>}
                    <span>
                        No
                    </span>
                </>
            </AppButton>
            <AppButton
                className="btn btn-primary"
                disabled={isDisabled }
                onClick={() => this.handleAccept(true)}
            >
                <>
                    {this.loading &&
                        <span className="spinner-border spinner-border-sm me-2"/>}
                    <span>
                        Yes
                    </span>
                </>
            </AppButton>
        </div>;
    }

    rejectAcceptFooterButtons(): React.JSX.Element
    {
        const isDisabled = this.isDisabled();
        return <div
            key={this.loading ? "1" : "0"}
        >
            <AppButton
                className="btn btn-secondary"
                disabled={isDisabled}
                onClick={() => this.handleAccept(false)}
            >
                <>
                    {this.loading &&
                        <span className="spinner-border spinner-border-sm me-2"/>}
                    <span>
                        Reject
                    </span>
                </>
            </AppButton>
            <AppButton
                className="btn btn-primary"
                disabled={isDisabled }
                onClick={() => this.handleAccept(true)}
            >
                <>
                    {this.loading &&
                        <span className="spinner-border spinner-border-sm me-2"/>}
                    <span>
                        Accept
                    </span>
                </>
            </AppButton>
        </div>;
    }

    backFooterButtons(): React.JSX.Element
    {
        return <>
            <AppButton
                key={this.loading ? "1" : "0"}
                className="btn btn-secondary"
                onClick={() => this.handleCancel()}
            >
                <>
                    {this.loading &&
                        <span className="spinner-border spinner-border-sm me-2"/>}
                    <span>
                        Back
                    </span>
                </>
            </AppButton>
        </>;
    }

    cancelFooterButtons(): React.JSX.Element
    {
        return <>
            <AppButton
                key={this.loading ? "1" : "0"}
                className="btn btn-secondary"
                onClick={() => this.handleCancel()}
            >
                <>
                    {this.loading &&
                        <span className="spinner-border spinner-border-sm me-2"/>}
                    <span>
                        Cancel
                    </span>
                </>
            </AppButton>
        </>;
    }

    submitFooterButtons(): React.JSX.Element
    {
        return <>
            <AppButton
                key={this.loading ? "1" : "0"}
                className="btn btn-primary"
                disabled={this.submitDisabled}
                onClick={() => this.handleAccept()}
            >
                <>
                    {this.loading &&
                        <span className="spinner-border spinner-border-sm me-2"/>}
                    <span>
                        Submit
                    </span>
                </>
            </AppButton>
        </>;
    }

    okFooterButtons(): React.JSX.Element
    {
        return <>
            <AppButton
                key={this.loading ? "1" : "0"}
                className="btn btn-primary"
                onClick={() => this.handleAccept()}
            >
                <>
                    {this.loading &&
                        <span className="spinner-border spinner-border-sm me-2"/>}
                    <span>
                    OK
                    </span>
                </>
            </AppButton>
        </>;
    }

    renderComponent() {

        const isDisabled = this.isDisabled();

        return <>
            <Modal
                show={!this.isHideModal}
                onHide={this.hideModal}
                centered={true}
                backdrop={isDisabled ? "static" : true}
                className="modalLittle"
            >

                <div className={isDisabled ? "content-disabled" : ""}>
                    <Modal.Header>
                        <h2>{this.modalTitle()}</h2>
                        <AppButton aria-label="Close Modal Window"
                                   className="btn ms-auto btn-smaller"
                                   onClick={() => this.handleCancel()}>
                            Close
                        </AppButton>
                    </Modal.Header>
                    <Modal.Body>
                        {this.renderBody()}
                    </Modal.Body>

                    {!this.hideFooter && <>
                        <Modal.Footer>
                            <div>
                                {this.renderFooterLeft()}
                            </div>
                            <div className="flex-grow-1 text-end">
                                {this.renderFooterRight()}
                            </div>
                        </Modal.Footer>
                    </>}
                </div>
            </Modal>
        </>
    }

}